import React from 'react';
import { format, utcToZonedTime } from 'date-fns-tz';
import { toast } from 'react-toastify';
import { API } from 'aws-amplify';
import { useHistory, useLocation } from 'react-router-dom';
import { t } from 'i18next';

import reducer from './reducer';
import useMoveOptions from './moveOptions';
import { ShipyardContext, ProtectedRouteContext } from '../../../Context';
import { userTypes } from '../../../../utils/enums';
import useE164 from '../../../../utils/E164';

const initialState = {
    isDoingAjax: true,
    isInititalzed: false,
    FirstName: '',
    LastName: '',
    CreatedUTC: '',
    UpdatedUTC: '',
    EmailAddress: '',
    UserName: '',
    PhoneNumber: '',
    UserType: 0,
    SchoolId: 0,
    Active: true,
    inError: [],
};

const blankOption = {
    id: 0,
    label: '',
};

/**Translate a UTC date string into a timezone via date-fns-tz
 * @param {string} date
 * @param {string} tz
 * @see {@link https://www.npmjs.com/package/date-fns-tz#utctozonedtime date-fns-tz} for further information.
 * @returns A formatted string with the date, time, and timezone.
 */
const translateDate = (date, tz) => {
    let options = { timeZone: tz };
    let _dateType = 'Pp z';
    return format(utcToZonedTime(new Date(date), tz), _dateType, options);
};

/**A React Hook for controlling the state of the UserSettingsOther UI.
 * @param {string} _userId The UserID parameter from the URL.
 * @returns {{
 * StateVariables: any, userTypeOptions: Array<object>, schoolOptions: Array<object>, activeOptions: Array<object>, isDoingAjax: boolean, isInitialized: boolean, retrieveUserSettings: function, updateUserSettings: function
 * }} An object containing the state variables and functions necessary for the UI. Examples of StateVariables include FirstName, PhoneNumber, UserName, etc.
 */
const useUserSettings = (_userId) => {
    const history = useHistory();
    const location = useLocation();

    const { userSettings } = React.useContext(ProtectedRouteContext);
    const { setMoveConfig, moveConfig, resetMoveConfig } = React.useContext(ShipyardContext);
    const moveOptions = useMoveOptions(_userId);

    const { normalize, toE164 } = useE164();

    const [userId] = React.useState(_userId);
    const [state, dispatch] = React.useReducer(reducer, initialState);
    const [userTypeOptions, setUserTypeOptions] = React.useState([blankOption]);
    const [schoolOptions, setSchoolOptions] = React.useState([blankOption]);
    const [activeOptions] = React.useState([
        {
            id: 0,
            label: 'Inactive',
            value: false,
        },
        {
            id: 1,
            label: 'Active',
            value: true,
        },
    ]);
    const [from] = React.useState(location.search.slice(1));

    /**
     * An asynchronous function that initializes the page if it has yet to be, and retrieved all pertinent information for the state.
     */
    const retrieveUserSettings = async () => {
        dispatch({ type: 'doingAjax' });

        if (!state.isInitialized) {
            resetMoveConfig();

            //TODO - Move these to the back/end database.
            let _userTypes = [
                blankOption,
                {
                    id: userTypes.Parent,
                    label: userSettings?.ClientOrgId === 5 ? t(`UserType.${userTypes.Parent}`) : 'Applicant',
                },
                {
                    id: userTypes.PrivateSchool,
                    label: t(`UserType.${userTypes.PrivateSchool}`),
                },
                {
                    id: userTypes.ScholarshipOrg,
                    label: userSettings?.ClientOrgId === 5 ? t(`UserType.${userTypes.ScholarshipOrg}`) : 'Admin',
                },
                {
                    id: userTypes.Admin,
                    label: t(`UserType.${userTypes.Admin}`),
                    disabled: userSettings.UserType < 4,
                },
            ];

            if (userSettings.ClientOrgId === 7)
                _userTypes = _userTypes.filter((opt) => opt.id !== userTypes.PrivateSchool && opt.id !== userTypes.Admin);
            setUserTypeOptions(_userTypes);
        }

        if (parseInt(userId) > 0) {
            try {
                let response = await API.get('ShipyardAPI', '/usersettings/' + userId);

                setSchoolOptions([
                    blankOption,
                    ...response?.schoolList?.map((school) => {
                        return {
                            id: school.SchoolID,
                            label: school.SchoolName,
                            disabled: school?.IsDisplayOnly,
                        };
                    }),
                ]);

                dispatch({
                    type: 'update',
                    newState: {
                        ...response?.userDetails,
                        CreatedUTC: response?.userDetails?.CreatedUTC
                            ? translateDate(response.userDetails.CreatedUTC, userSettings.ClientTimeZone)
                            : 'N/A',
                        UpdatedUTC: response?.userDetails?.UpdatedUTC
                            ? translateDate(response.userDetails.UpdatedUTC, userSettings.ClientTimeZone)
                            : 'N/A',
                        PhoneNumber: response?.userDetails?.Notifications?.Sms ? response.userDetails.Notifications.Sms : '',
                        EmailAddress: response?.userDetails?.Notifications?.Email ? response.userDetails.Notifications.Email : '',
                        DynamicHeaderText: `${response?.userDetails?.FirstName} ${response?.userDetails?.LastName}`,
                        isDoingAjax: false,
                        isInitialized: true,
                    },
                });
            } catch (e) {
                toast.error('API error.');
            } finally {
                dispatch({ type: 'ajaxDone' });
            }
        } else {
            try {
                let _response = await API.get('ShipyardAPI', '/sitesettings/schools');

                setSchoolOptions([
                    blankOption,
                    ..._response?.schoolsList?.map((school) => {
                        return {
                            id: school.SchoolID,
                            label: school.SchoolName,
                            disabled: school?.IsDisplayOnly,
                        };
                    }),
                ]);

                dispatch({
                    type: 'update',
                    newState: {
                        DynamicHeaderText: 'New User',
                        isDoingAjax: false,
                        isInitialized: true,
                    },
                });
            } catch (e) {
                toast.error('API error.');
            } finally {
                dispatch({ type: 'ajaxDone' });
            }
        }
    };

    /**
     * Creates a Request Body object that has shared props between create and update functions.
     * @returns A Body object that translates the current state of the page into the necessary endpoint parameters.
     */
    const createRequestBody = () => {
        return {
            firstName: state?.FirstName,
            lastName: state?.LastName,
            isActive: state?.Active,
            email: state?.EmailAddress,
            languagePreference: 'en-US',
            phone: toE164(state?.PhoneNumber),
            notificationBySms: state?.Notifications?.BySms || false,
            notificationByEmail: state?.Notifications?.ByEmail || true,
            schoolId: !!state?.SchoolId ? state.SchoolId : null,
            userType: state?.UserType,
        };
    };

    /**A wrapper function for invoking the dispatch from the reducer function.
     * @param {string} key The associated key in the state, i.e. FirstName, PhoneNumber, etc.
     * @param {string} value The new value to update the state with.
     */
    const updateUserSettings = (key, value) => {
        dispatch({
            type: 'update',
            newState: {
                [key]: value,
                inError: state.inError.filter((opt) => opt.field !== key),
            },
        });
    };

    /**A wrapper function for invoking the correct state changes and history pushes for the Move Record action. */
    const moveOwnership = () => {
        setMoveConfig({ ...moveConfig, ...moveOptions });
        history.push('/actions/move-record');
    };

    /**A wrapper function for handling the history stack push that leads to the correct previous view. */
    const goBack = () => {
        switch (userSettings.UserType) {
            case 4:
                history.push(`/sitesettings?${from}`);
                return;
            default:
                history.push(`/settings?${from}`);
                return;
        }
    };

    const newUserProcess = async () => {
        dispatch({ type: 'doingAjax' });

        try {
            await API.post('ShipyardAPI', '/usersettings', {
                body: createRequestBody(),
            });
            toast.success(t('General.ChangesSaved'));
            goBack?.();
        } catch (postError) {
            console.error(postError);
            dispatch({ type: 'ajaxDone' });
            toast.error(!!postError?.response?.data?.message ? t(postError.response.data.message) : t('General.AjaxError'));
            if (postError?.response?.data?.inError instanceof Array) {
                dispatch({
                    type: 'update',
                    newState: {
                        inError: postError.response.data.inError.map((item) => {
                            return {
                                ...item,
                            };
                        }),
                    },
                });
            }
        }
    };

    const updateUserProcess = async () => {
        dispatch({ type: 'doingAjax' });
        try {
            await API.patch('ShipyardAPI', '/usersettings/' + userId, {
                body: createRequestBody(),
            });
            toast.success(t('General.ChangesSaved'));
        } catch (postError) {
            console.error(postError);
            toast.error(!!postError?.response?.data?.message ? t(postError.response.data.message) : t('General.AjaxError'));
            if (postError?.response?.data?.inError instanceof Array) {
                dispatch({
                    type: 'update',
                    newState: {
                        inError: postError.response.data.inError.map((item) => {
                            return {
                                ...item,
                            };
                        }),
                    },
                });
            }
        } finally {
            dispatch({ type: 'ajaxDone' });
        }
    };

    const deleteUser = async () => {
        if (!state.IsActive) {
            toast.error('Reactivating users not implemented yet.');
            return;
        }

        dispatch({ type: 'doingAjax' });
        try {
            await API.del('ShipyardAPI', '/usersettings/' + userId);
            toast.success(t('UserSettings.Other.Deleted'));
            goBack?.();
        } catch (delError) {
            console.error(delError);
            toast.error(t('General.AjaxError'));
        } finally {
            dispatch({ type: 'ajaxDone' });
        }
    };

    return {
        ...state,
        userSettings,
        userTypeOptions,
        schoolOptions,
        activeOptions,
        normalizePhone: normalize,
        retrieveUserSettings,
        updateUserSettings,
        moveOwnership,
        goBack,
        deleteUser,
        newUserProcess,
        updateUserProcess,
    };
};

export default useUserSettings;
